import { useDispatch, useSelector } from 'react-redux';
import { setActiveProvider } from '../features/providers/providersSlice';
import SWIcon from './SWIcon';
import requestAppointmentLinkBuilder from '../utilities/requestAppointmentLinkBuilder';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import React from 'react';
import normalizeId from '../utilities/normalizeId';
import { setZoom } from '../features/providers/zoomSlice';
import Tooltip from 'react-simple-tooltip';

const BuildingIcon = () => (
  <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 2.5C11 1.67157 10.3284 1 9.5 1H2.5C1.67157 1 1 1.67157 1 2.5V15.5H0V2.5C0 1.11929 1.11929 0 2.5 0H9.5C10.8807 0 12 1.11929 12 2.5V15.5H11V2.5Z" fill="#769198" />
    <path d="M2.5 2.5H4.5V4.5H2.5V2.5Z" fill="#769198" />
    <path d="M2.5 5.5H4.5V7.5H2.5V5.5Z" fill="#769198" />
    <path d="M2.5 8.5H4.5V10.5H2.5V8.5Z" fill="#769198" />
    <path d="M2.5 11.5H4.5V13.5H2.5V11.5Z" fill="#769198" />
    <path d="M7.5 2.5H9.5V4.5H7.5V2.5Z" fill="#769198" />
    <path d="M7.5 5.5H9.5V7.5H7.5V5.5Z" fill="#769198" />
    <path d="M7.5 8.5H9.5V10.5H7.5V8.5Z" fill="#769198" />
    <path d="M7.5 11.5H9.5V13.5H7.5V11.5Z" fill="#769198" />
  </svg>

);

const PinIcon = () => (
  <svg width="11" height="15" viewBox="0 0 11 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.96056 0.0078123C4.55142 0.0562881 4.35002 0.0915883 4.05692 0.166124C2.54919 0.549646 1.26666 1.58551 0.560486 2.99002C0.0174868 4.06999 -0.138085 5.33601 0.124932 6.53471C0.521717 8.34289 1.79037 10.6642 3.78916 13.2391C4.24054 13.8206 4.87667 14.592 5.03656 14.7517C5.25174 14.9667 5.54388 14.9667 5.75906 14.7517C5.91894 14.592 6.55508 13.8206 7.00646 13.2391C9.00525 10.6642 10.2739 8.34289 10.6707 6.53471C10.7633 6.11271 10.795 5.80731 10.794 5.34939C10.7881 2.73639 8.86667 0.495806 6.25956 0.06171C6.00097 0.018656 5.16266 -0.0161195 4.96056 0.0078123ZM5.69006 2.70299C6.034 2.74563 6.24836 2.80489 6.52354 2.93341C7.29618 3.29432 7.82711 3.93832 8.03862 4.77115C8.08188 4.94147 8.09357 5.08638 8.09153 5.42702C8.08919 5.81276 8.07972 5.89446 8.0111 6.12066C7.77607 6.89526 7.28382 7.48586 6.57816 7.83988C6.02103 8.1194 5.23606 8.1793 4.61447 7.98968C3.69943 7.7106 3.04398 7.02701 2.76889 6.06493C2.67961 5.75262 2.6736 5.09955 2.757 4.77115C3.04092 3.65312 3.93823 2.85022 5.06541 2.70558C5.35131 2.66888 5.41285 2.66865 5.69006 2.70299Z" fill="#769198" />
  </svg>
);

const SingleResult = (p) => {
  const { provider, overrideId, className = '' } = p;
  const activeProvider = useSelector((state) => {
    return state.providers.activeProvider;
  });

  const {
    id,
    distance,
    first_name,
    last_name,
    suffix,
    practice_name,
    location,
    headshot,
    permalink,
    type_icon,
    ossio_type,
    specialties,
    additional_properties,
  } = provider;

  const actualId = id ? id : overrideId;
  const {
    city = '',
    state = '',
    street_address = '',
    street_address_2 = '',
    zip = '',
    practice_name: inner_practice_name,
  } = location;

  const actual_practice_name = inner_practice_name
    ? inner_practice_name
    : practice_name;

  const isActive = actualId === normalizeId(activeProvider);

  const dispatch = useDispatch();
  const contactUrl = requestAppointmentLinkBuilder(permalink);

  const OSSIO_LEVEL_COLORS = {
    'ossiofiber-center-of-excellence': '#F4A71C',
    'ossiofiber-certified-surgeon': '#5C899A',
    'ossiofiber-elite-center-of-excellence': '#635C55',
  };

  const OSSIO_LEVEL_BG_COLORS = {
    'ossiofiber-center-of-excellence': '#F4A71C0F',
    'ossiofiber-certified-surgeon': '#7691980F',
    'ossiofiber-elite-center-of-excellence': '#635C550F',
  };

  const ACTIVE_BG_FALLBACK = '#61BA460F';

  const ADDITIONAL_PROPERTY_COLORS = {
    // bunion related
    'metal-free-bunion-correction-provider': '#63A659',
    'bunion': '#63A659',
  };

  const SPECIALTY_COLORS = {
    // knee related
    'knee': '#618AA6',
    'kneebar': '#618AA6',
  };

  const FALLBACK_COLOR = '#618AA6';

  const getOssioColor = (slug) =>
    OSSIO_LEVEL_COLORS[slug] || FALLBACK_COLOR;

  const getAdditionalPropertyColor = (slug) =>
    ADDITIONAL_PROPERTY_COLORS[slug] || FALLBACK_COLOR;

  const getSpecialtyColor = (slug) =>
    SPECIALTY_COLORS[slug] || FALLBACK_COLOR;

  const ossioColor = getOssioColor(ossio_type?.slug);
  const ossioBgColor = ossio_type?.slug && OSSIO_LEVEL_BG_COLORS[ossio_type.slug]
    ? OSSIO_LEVEL_BG_COLORS[ossio_type.slug]
    : ACTIVE_BG_FALLBACK;

  return (
    <div
      onClick={() => {
        dispatch(setActiveProvider(actualId));
        if (isNaN(distance)) {
          dispatch(setZoom(13));
        }
      }}
      className={`${className} ${isActive
        ? 'provider-active'
        : 'text-dark provider-inactive px-[20px]'
        } w-full max-w-full lg:w-fit box-border px-[20px] cursor-pointer py-[24px] rounded-xl provider-card flex flex-col mb-5 overflow-hidden lg:overflow-visible`}
      style={{ '--hover-bg': ossioBgColor, ...(isActive ? { backgroundColor: ossioBgColor } : {}) }}
    >
      {/* ── Main row: Photo + Content ── */}
      <div className="flex items-start gap-4 flex-col lg:flex-row">

        {/* LEFT: Headshot with badge icons */}
        <div className="relative shrink-0 self-center z-10 w-full lg:w-auto">
          <div
            className="relative w-full h-[230px] max-h-[230px] lg:w-[160px] lg:h-[183px] lg:max-h-none rounded-xl overflow-hidden bg-white"
            style={{
              border: `3px solid ${type_icon ? ossioColor : '#61BA46'}`,
              borderRadius: '0.75rem',
            }}
          >

            {/* actual image */}
            <LazyLoadImage
              className="relative z-10 w-full h-full object-contain object-center lg:object-cover lg:object-center provider-headshot"
              src={headshot}
              alt={`${first_name} ${last_name}${suffix}`}
            />
          </div>

          <div
            className="hidden lg:absolute lg:z-50 lg:flex lg:flex-col lg:gap-2"
            style={{ top: '50%', transform: 'translateY(-30%)', left: '-17px' }}
          >

            {/* type_icon (ossio level) */}
            {type_icon && (
              <div className="relative group">
                <span className="w-[30px] h-[30px] flex items-center justify-center cursor-pointer relative z-[9999]">
                  <SWIcon
                    icon={type_icon}
                    height={32}
                    width={'auto'}
                    alt={ossio_type?.name}
                    className="max-w-full max-h-full w-auto h-auto block"
                  />
                </span>
                <div
                  className="absolute flex items-center pointer-events-none whitespace-nowrap z-[9998] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                  style={{
                    top: '50%',
                    left: '0px',
                    transform: 'translateY(-50%)',
                    backgroundColor: ossioColor,
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    height: '30px',
                    paddingLeft: '0px',
                    paddingRight: '50px',
                    borderRadius: '999px',
                  }}
                >
                  <span style={{ transform: 'translate(36px, 2px)' }}>{ossio_type?.name}</span>
                </div>
              </div>
            )}

            {/* Additional properties */}
            {Array.isArray(additional_properties) && additional_properties.map(({ name, icon, icon_inverted, slug }, index) => {
              const apColor = getAdditionalPropertyColor(slug);
              return icon && (
                <div key={`ap-${index}`} className="relative group">
                  <span className="w-[30px] h-[30px] flex items-center justify-center cursor-pointer relative z-[9999]">
                    <img
                      src={isActive ? (icon_inverted || icon) : icon}
                      alt={name}
                      style={{ width: 32, height: 'auto' }}
                    />
                  </span>
                  <div
                    className="absolute flex items-center pointer-events-none whitespace-nowrap z-[9998] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    style={{
                      top: '50%',
                      left: '0px',
                      transform: 'translateY(-50%)',
                      backgroundColor: apColor,
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      height: '30px',
                      paddingLeft: '0px',
                      paddingRight: '50px',
                      borderRadius: '999px',
                    }}
                  >
                    {/* <span style={{ transform: 'translateX(15px)' }}>{name}</span> */}
                    <span style={{ transform: 'translate(36px, 2px)' }}>{name}</span>
                  </div>
                </div>
              );
            })}

            {/* Specialties */}
            {Array.isArray(specialties) && specialties.map(({ icon, icon_inverted, name, slug }, index) => {
              const spColor = getSpecialtyColor(slug);
              return (icon || icon_inverted) && (
                <div key={`sp-${index}`} className="relative group">
                  <span className="w-[30px] h-[30px] flex items-center justify-center cursor-pointer relative z-[9999]">
                    <img
                      src={isActive ? (icon_inverted || icon) : (icon || icon_inverted)}
                      alt={name}
                      style={{ width: 32, height: 'auto' }}
                    />
                  </span>
                  <div
                    className="absolute flex items-center pointer-events-none whitespace-nowrap z-[9998] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    style={{
                      top: '50%',
                      left: '0px',
                      transform: 'translateY(-50%)',
                      backgroundColor: spColor,
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      height: '30px',
                      paddingLeft: '0px',
                      paddingRight: '50px',
                      borderRadius: '999px',
                    }}
                  >
                    <span style={{ transform: 'translate(36px, 2px)' }}>{name.replace(/&amp;/g, '&')}</span>

                  </div>
                </div>
              );
            })}



          </div>
        </div>


        {/* Content block: info on left, buttons on right — same row */}
        <div className="flex flex-1 flex-col items-start justify-between gap-4 min-w-0">

          {/* Info column */}
          <div className="flex flex-col gap-1 min-w-0">
            {/* Distance */}
            {!isNaN(distance) && (
              <p className={`font-extrabold distance-label mt-[10px] ${isActive ? 'text-[20px] text-black' : 'text-[12px] text-ossio-green'}`}>
                {parseFloat(distance).toFixed(2)} miles
              </p>
            )}

            {/* Name */}
            <h1
              className={`font-extrabold text-[24px] mb-[10px] mt-[-10px] ${isActive ? 'text-[#61BA46]' : 'text-[#61BA46]'
                }`}
            >
              {first_name} {last_name}{suffix}
            </h1>

            {/* Mobile badges under name */}
            <div className=" lg:hidden flex-col gap-[8px] mb-[18px] w-full mobile_badges">
              {type_icon && ossio_type?.name && (
                <div
                  className="inline-flex w-fit items-center gap-[8px] rounded-full pr-[10px] text-white text-[12px] font-bold leading-none"
                  style={{ backgroundColor: ossioColor }}
                >
                  <span className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-white/20 shrink-0">
                    <SWIcon
                      icon={type_icon}
                      height={30}
                      width="auto"
                      alt={ossio_type?.name}
                      className="max-w-full max-h-full w-auto h-auto block"
                    />
                  </span>

                  <span>{ossio_type?.name}</span>
                </div>
              )}

              {Array.isArray(additional_properties) &&
                additional_properties.map(({ name, icon, icon_inverted, slug }, index) => {
                  const apColor = getAdditionalPropertyColor(slug);

                  return icon && (
                    <div
                      key={`mobile-ap-${index}`}
                      className="inline-flex w-fit items-center gap-[8px] rounded-full pr-[10px] text-white text-[12px] font-bold leading-none"
                      style={{ backgroundColor: apColor }}
                    >
                      <span className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-white/20 shrink-0">
                        <img
                          src={isActive ? icon_inverted || icon : icon}
                          alt={name}
                          className="w-[30px] h-auto block"
                        />
                      </span>

                      <span>{name}</span>
                    </div>
                  );
                })}

              {Array.isArray(specialties) &&
                specialties.map(({ icon, icon_inverted, name, slug }, index) => {
                  const spColor = getSpecialtyColor(slug);

                  return (icon || icon_inverted) && (
                    <div
                      key={`mobile-sp-${index}`}
                      className="inline-flex w-fit items-center gap-[8px] rounded-full pr-[10px] text-white text-[12px] font-bold leading-none"
                      style={{ backgroundColor: spColor }}
                    >
                      <span className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-white/20 shrink-0">
                        <img
                          src={isActive ? icon_inverted || icon : icon || icon_inverted}
                          alt={name}
                          className="w-[30px] h-auto block"
                        />
                      </span>

                      <span>{name.replace(/&amp;/g, '&')}</span>
                    </div>
                  );
                })}
            </div>

            {/* Practice name */}
            {actual_practice_name && (
              <div className={`flex items-start gap-2 text-sm ${isActive ? 'text-[#769198]' : 'text-[#769198]'}`}>
                <BuildingIcon />
                <span>{actual_practice_name}</span>
              </div>
            )}

            {/* Address */}
            <div className={`flex items-start gap-2 text-sm ${isActive ? 'text-[#769198]' : 'text-[#769198]'}`}>
              <PinIcon />
              <span>
                {street_address}
                {street_address_2 ? ` ${street_address_2}` : ''} {city}, {state}, <span className="text-[#61BA46]">{zip}</span>
              </span>
            </div>
          </div>

          {/* Buttons — stacked vertically on the right, vertically centered */}
          <div className="flex items-stretch gap-3 shrink-0 justify-start flex-wrap">
            <a
              href={permalink}
              onClick={(e) => e.stopPropagation()}
              className={`inline-flex items-center justify-center rounded-full px-[10px] lg:px-5 py-2 text-sm font-semibold border-2 transition-colors duration-150 whitespace-nowrap ${isActive
                ? 'border-ossio-green text-ossio-green bg-white hover:bg-green-50'
                : 'border-ossio-green text-ossio-green bg-white hover:bg-green-50'
                }`}
            >
              Provider Information
            </a>
            <a
              href={contactUrl}
              onClick={(e) => e.stopPropagation()}
              className={`inline-flex items-center justify-center rounded-full px-[10px] lg:px-5 py-2 text-sm font-semibold transition-colors duration-150 whitespace-nowrap ${isActive
                ? 'bg-ossio-green text-white hover:bg-green-700'
                : 'bg-ossio-green text-white hover:bg-green-700'
                }`}
            >
              Request Appointment
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SingleResult;